/* eslint-disable no-unused-vars */

/**
 * Enum for canonical rink identifiers. This allows us to treat different
 * string representations of the same location as equal.
 */
export enum Rink {
    AllSeasons,
    Kentwood,
    JohnnysEast,
    JohnnysWest,
    TheEdge,
    Patterson,
    MillsIceZone,
    Affton,
    ArcticIceArena,
    PleasantPrairie,
    MountProspect,
    NorthShore,
    FoxValley,
    WestDundee,
    Oakton,
    PolarDome,
    McCannIceArena,
    StClairShores,
    BigBoyArena,
    BarringtonIceArena,
    BlackhawksIceCenter,
    Rosemont,
    EaglesIceCenter,
    GriffsIcehouse,
    GriffsGeorgetown,
    WalkerIce,
    SouthsideArena,
    LawsonIceArena,
    WingsEventCenter,
    TopShelf,
    DarienSportsplex,
    MaryvilleUHC,
    LakeForestAcademy,
    CentennialIceArena,
    Orbit,
    Heartland,
    WestMeadows,
    ElkGrove,
    Unknown,
}

export interface RinkSheetInfo {
    key: string;
    displayName: string;
    aliases?: string[];
    livebarn?: string;
}

export interface RinkMetadata {
    aliases: string[];
    displayName: string;
    mapsUrl?: string;
    timezone: string;
    address?: string;
    sheets?: RinkSheetInfo[];
    livebarn?: Record<string, string> | null;
}

/**
 * Holds the metadata for each rink, including aliases, display name, maps URL,
 * sheet options, and LiveBarn information.
 */
export const RINK_DATA = new Map<Rink, RinkMetadata>([
    [Rink.AllSeasons, {
        aliases: ['all seasons'],
        displayName: 'All Seasons Ice Rinks',
        mapsUrl: 'https://maps.app.goo.gl/FAL2BwuxZCFqgFtQA',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'red', displayName: 'Red Rink', aliases: ['red rink', 'red'], livebarn: 'https://watch.livebarn.com/en/video/4481' },
            { key: 'blue', displayName: 'Blue Rink', aliases: ['blue rink', 'blue'], livebarn: 'https://watch.livebarn.com/en/video/4480' },
        ],
        livebarn: { red: 'https://watch.livebarn.com/en/video/4481', blue: 'https://watch.livebarn.com/en/video/4480' }
    }],
    [Rink.Kentwood, { aliases: ['kentwood', 'kentwood ice arena'], displayName: 'Kentwood Ice Arena', address: '6230 Kalamazoo Ave SE, Grand Rapids, MI 49508', mapsUrl: 'https://goo.gl/maps/gYjWNyzMJxutQx6KA', timezone: 'America/Detroit', livebarn: { default: 'https://watch.livebarn.com/en/video/4544' } }],
    [Rink.JohnnysEast, { aliases: ['jih', "johnny's east", "johnnys east", "1350 west madison street"], displayName: "Johnny's East", mapsUrl: 'https://maps.app.goo.gl/9ZRynHHWKJwSn5c67', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/588' } }],
    [Rink.JohnnysWest, { aliases: ["johnny's west", "johnnys west", 'jw', 'jwt'], displayName: "Johnny's West", mapsUrl: 'https://maps.app.goo.gl/i8fk4qQ8KcnzGNs3A', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/595' } }],
    [Rink.TheEdge, { aliases: ['the edge', 'edge ice'], displayName: 'The Edge Ice Arena', mapsUrl: 'https://maps.app.goo.gl/eq445SepTuou7sYH7', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/575', west: 'https://watch.livebarn.com/en/video/575' } }],
    [Rink.Patterson, { aliases: ['patterson', 'patterson ice center'], displayName: 'Patterson Ice Center', address: '2550 Patterson Ave SE, Grand Rapids, MI 49546', mapsUrl: 'https://goo.gl/maps/Qc9eEDFdjE3sREgTA', timezone: 'America/Detroit', livebarn: null }],
    [Rink.MillsIceZone, { aliases: ['mills ice zone'], displayName: 'Mills Ice Zone', mapsUrl: 'https://maps.app.goo.gl/9qMdXdh9LGx356oM6', timezone: 'America/Chicago', livebarn: null }],
    [Rink.Affton, { aliases: ['affton'], displayName: 'Affton Ice Rink', mapsUrl: 'https://maps.app.goo.gl/YpWq5dY5Z5X5Z6Z6A', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/992' } }],
    [Rink.ArcticIceArena, { aliases: ['arctic ice arena', 'arctic'], displayName: 'Arctic Ice Arena', mapsUrl: 'https://maps.app.goo.gl/sZUxD7L2ajVxLFGj8', timezone: 'America/Chicago', livebarn: { championship: 'https://watch.livebarn.com/en/video/2456', default: 'https://watch.livebarn.com/en/video/2456' } }],
    [Rink.PleasantPrairie, {
        aliases: ['pleasant prairie'],
        displayName: 'Pleasant Prairie RecPlex',
        mapsUrl: 'https://maps.app.goo.gl/sZUxD7L2ajVxLFGj8',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'green', displayName: 'Green Rink', aliases: ['green rink', 'green'], livebarn: 'https://watch.livebarn.com/en/video/730' },
            { key: 'blue', displayName: 'Blue Rink', aliases: ['blue rink', 'blue'], livebarn: 'https://watch.livebarn.com/en/video/729' },
        ],
        livebarn: { green: 'https://watch.livebarn.com/en/video/730', blue: 'https://watch.livebarn.com/en/video/729' }
    }],
    [Rink.MountProspect, { aliases: ['mount prospect'], displayName: 'Mount Prospect Ice Arena', mapsUrl: 'https://maps.app.goo.gl/sZUxD7L2ajVxLFGj8', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/327', north: 'https://watch.livebarn.com/en/video/327' } }],
    [Rink.NorthShore, { aliases: ['north shore'], displayName: 'North Shore Ice Arena', mapsUrl: 'https://maps.app.goo.gl/sZUxD7L2ajVxLFGj8', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/204' } }],
    [Rink.FoxValley, { aliases: ['fox valley'], displayName: 'Fox Valley Ice Arena', mapsUrl: 'https://maps.app.goo.gl/sZUxD7L2ajVxLFGj8', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/68', north: 'https://watch.livebarn.com/en/video/68' } }],
    [Rink.WestDundee, { aliases: ['west dundee'], displayName: 'West Dundee - Leafs Ice Centre', mapsUrl: 'https://maps.app.goo.gl/sZUxD7L2ajVxLFGj8', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/40', jj: 'https://watch.livebarn.com/en/video/40' } }],
    [Rink.Oakton, { aliases: ['oakton'], displayName: 'Oakton Ice Arena', mapsUrl: 'https://maps.app.goo.gl/sZUxD7L2ajVxLFGj8', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/239', main: 'https://watch.livebarn.com/en/video/239' } }],
    [Rink.PolarDome, { aliases: ['polar dome'], displayName: 'Polar Dome Ice Arena', mapsUrl: 'https://maps.app.goo.gl/nXnEde3xUeCoeygk7', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/5259' } }],
    [Rink.McCannIceArena, { aliases: ['liggett', 'mccann ice arena'], displayName: 'McCann Ice Arena', address: '1045 Cook Road, Grosse Pointe Woods, MI 48236, USA', timezone: 'America/Detroit', livebarn: { default: 'https://watch.livebarn.com/en/video/4185' } }],
    [Rink.StClairShores, {
        aliases: ['scs', 'st clair shores', 'st. clair shores'],
        displayName: 'St. Clair Shores Civic Arena',
        address: '20000 Stephens St, St. Clair Shores, MI 48080, United States',
        timezone: 'America/Detroit',
        sheets: [
            { key: 'gardens', displayName: 'Gardens Rink', aliases: ['gardens rink', 'gardens'], livebarn: 'https://watch.livebarn.com/en/video/1104' },
            { key: 'wells', displayName: 'Wells Rink', aliases: ['wells rink', 'wells'], livebarn: 'https://watch.livebarn.com/en/video/1103' },
        ],
        livebarn: { gardens: 'https://watch.livebarn.com/en/video/1104', wells: 'https://watch.livebarn.com/en/video/1103' }
    }],
    [Rink.BigBoyArena, {
        aliases: ['bba'],
        displayName: 'Big Boy Arena',
        address: '34400 Utica Rd, Fraser, MI, USA',
        timezone: 'America/Detroit',
        sheets: [
            { key: 'priority', displayName: 'Priority Rink', aliases: ['priority rink', 'priority'], livebarn: 'https://watch.livebarn.com/en/video/579' },
            { key: 'rink 3', displayName: 'Rink 3', aliases: ['rink 3', 'rink3', 'sheet 3'], livebarn: 'https://watch.livebarn.com/en/video/576' },
            { key: 'rink 4', displayName: 'Rink 4', aliases: ['rink 4', 'rink4', 'sheet 4'], livebarn: 'https://watch.livebarn.com/en/video/577' },
            { key: 'rink 5', displayName: 'Rink 5', aliases: ['rink 5', 'rink5', 'sheet 5'], livebarn: 'https://watch.livebarn.com/en/video/580' },
        ],
        livebarn: { priority: 'https://watch.livebarn.com/en/video/579', 'rink 3': 'https://watch.livebarn.com/en/video/576', 'rink 4': 'https://watch.livebarn.com/en/video/577', 'rink 5': 'https://watch.livebarn.com/en/video/580' }
    }],
    [Rink.BarringtonIceArena, { aliases: ['barrington ice arena'], displayName: 'Barrington Ice Arena', address: '28205 W Commercial Ave, Lake Barrington, IL 60010', timezone: 'America/Chicago', livebarn: { default: 'https://watch.livebarn.com/en/video/1170'} }],
    [Rink.BlackhawksIceCenter, {
        aliases: ['bic', 'blackhawks ice center', 'blackhawks', 'fifth third', '1801 w jackson', '1801 west jackson', 'usg arena'],
        displayName: 'Blackhawks Ice Center',
        address: '1801 W Jackson Blvd, Chicago, IL 60612',
        mapsUrl: 'https://maps.app.goo.gl/6qEC2bAzCa87reup9',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'rink 1', displayName: 'Rink 1', aliases: ['rink 1', 'rink1', 'sheet 1'], livebarn: 'https://watch.livebarn.com/en/video/665' },
            { key: 'rink 2', displayName: 'Rink 2', aliases: ['rink 2', 'rink2', 'sheet 2'], livebarn: 'https://watch.livebarn.com/en/video/666' },
            { key: 'rink 3', displayName: 'Rink 3', aliases: ['rink 3', 'rink3', 'sheet 3'], livebarn: 'https://watch.livebarn.com/en/video/5802' },
            { key: 'rink 4', displayName: 'USG Arena (Rink 4)', aliases: ['rink 4', 'rink4', 'sheet 4', 'usg arena', 'usg'], livebarn: 'https://watch.livebarn.com/en/video/5803' },
        ],
        livebarn: {
            'rink 1': 'https://watch.livebarn.com/en/video/665',
            'rink 2': 'https://watch.livebarn.com/en/video/666',
            'rink 3': 'https://watch.livebarn.com/en/video/5802',
            'rink 4': 'https://watch.livebarn.com/en/video/5803',
            'usg': 'https://watch.livebarn.com/en/video/5803',
        }
    }],
    [Rink.Rosemont, {
        aliases: ['rosemont', 'rosemont ice arena', '7115 barry'],
        displayName: 'Rosemont Ice Arena',
        address: '7115 Barry St, Rosemont, IL 60018',
        mapsUrl: 'https://maps.app.goo.gl/1FNT4Zp5SYvnJwCt9',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'east', displayName: 'East Rink', aliases: ['east rink', 'east', 'rosemont east'], livebarn: 'https://watch.livebarn.com/en/video/5672' },
            { key: 'west', displayName: 'West Rink', aliases: ['west rink', 'west', 'rosemont west'], livebarn: 'https://watch.livebarn.com/en/video/5673' },
        ],
        livebarn: {
            'east': 'https://watch.livebarn.com/en/video/5672',
            'west': 'https://watch.livebarn.com/en/video/5673',
        }
    }],
    [Rink.EaglesIceCenter, {
        aliases: ['eagles ice center', 'eagles,', 'eagles ice', 'eagles'],
        displayName: 'Eagles Ice Center',
        address: '2600 Village Dr SE, Grand Rapids, MI 49506',
        mapsUrl: 'https://goo.gl/maps/QnxRQTYpUVwKzjub7',
        timezone: 'America/Detroit',
        livebarn: {
            default: 'https://watch.livebarn.com/en/video/1007',
        },
    }],
    [Rink.GriffsIcehouse, {
        aliases: [
            "griff's icehouse at belknap park",
            "griffs icehouse at belknap park",
            "griff's ice house west",
            "griff's ice house east",
            "griff's icehouse west",
            "griff's icehouse east",
            "griffs icehouse west",
            "griffs icehouse east",
            "griff's icehouse",
            "griffs icehouse",
            "griff's ice house",
            "griffs ice house",
            "griff's i.h., west",
            "griff's i.h., east",
            "griff's i.h.",
            "griffs i.h.",
            "griff's ih, west",
            "griff's ih, east",
            "griff's ih",
            "griffs ih",
            "griff's, west",
            "griff's, east",
            "griffs, west",
            "griffs, east",
            "griff's west",
            "griff's east",
            "griffs west",
            "griffs east",
            "griff's",
            "griffs",
            'belknap',
        ],
        displayName: "Griff's Icehouse at Belknap Park",
        address: '30 Coldbrook St NE, Grand Rapids, MI 49503',
        mapsUrl: 'https://maps.app.goo.gl/BQfdNDLZ9TxpUuTz9',
        timezone: 'America/Detroit',
        sheets: [
            { key: 'east', displayName: 'East Rink', aliases: ['east rink', 'east', "griff's east", "griffs east"], livebarn: 'https://watch.livebarn.com/en/video/772' },
            { key: 'west', displayName: 'West Rink', aliases: ['west rink', 'west', "griff's west", "griffs west"], livebarn: 'https://watch.livebarn.com/en/video/771' },
        ],
        livebarn: {
            'east rink': 'https://watch.livebarn.com/en/video/772',
            'west rink': 'https://watch.livebarn.com/en/video/771',
            'east': 'https://watch.livebarn.com/en/video/772',
            'west': 'https://watch.livebarn.com/en/video/771',
        },
    }],
    [Rink.GriffsGeorgetown, {
        aliases: ["griff's georgetown", "griffs georgetown", "griff's georgetown ice arena"],
        displayName: "Griff's Georgetown Ice Arena",
        address: '8500 48th Ave, Hudsonville, MI 49426',
        mapsUrl: 'https://goo.gl/maps/75Pa7iMFUHWJfyxu6',
        timezone: 'America/Detroit',
        livebarn: null,
    }],
    [Rink.WalkerIce, {
        aliases: ['walker ice', 'walker ice and fitness', 'walker'],
        displayName: 'Walker Ice and Fitness',
        address: '4151 Remembrance Rd NW, Grand Rapids, MI 49534',
        mapsUrl: 'https://goo.gl/maps/hP8F1X53r3jPvS5NA',
        timezone: 'America/Detroit',
        livebarn: null,
    }],
    [Rink.SouthsideArena, {
        aliases: ['southside arena', 'southside'],
        displayName: 'Southside Arena',
        address: '566 100th St SW, Byron, MI 49315',
        mapsUrl: 'https://goo.gl/maps/tGEUiD4a2vQRsh4J7',
        timezone: 'America/Detroit',
        livebarn: null,
    }],
    [Rink.LawsonIceArena, {
        aliases: ['lawson', 'lawson ice arena'],
        displayName: 'Lawson Ice Arena',
        address: 'Recreation Drive, Kalamazoo, MI 49008',
        mapsUrl: 'https://goo.gl/maps/ggckLBusZrehuJoYA',
        timezone: 'America/Detroit',
        livebarn: null,
    }],
    [Rink.WingsEventCenter, {
        aliases: ['wings event center', 'wings'],
        displayName: 'Wings Event Center',
        address: '3600 Vanrick Dr, Kalamazoo, MI 49001',
        mapsUrl: 'https://goo.gl/maps/uuc8vxjn9cm1UZ798',
        timezone: 'America/Detroit',
        livebarn: null,
    }],
    [Rink.TopShelf, {
        aliases: ['top shelf', 'top shelf ice arena', 'top shelf ice', 'top shelf arena', '6152 w grand'],
        displayName: 'Top Shelf Ice Arena',
        address: '6152 W Grand Ave, Gurnee, IL 60031',
        mapsUrl: 'https://maps.google.com/?q=Top+Shelf+Ice+Arena+6152+W+Grand+Ave+Gurnee+IL',
        timezone: 'America/Chicago',
        livebarn: { default: 'https://watch.livebarn.com/en/video/2147' },
    }],
    [Rink.DarienSportsplex, {
        aliases: ['darien sportsplex', 'darien sports plex', 'darien', '451 plainfield rd'],
        displayName: 'Darien Sportsplex',
        address: '451 Plainfield Rd, Darien, IL 60561',
        mapsUrl: 'https://maps.google.com/?q=Darien+Sportsplex+451+Plainfield+Rd+Darien+IL',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'campbell', displayName: 'Campbell Rink', aliases: ['campbell rink', 'campbell'], livebarn: 'https://watch.livebarn.com/en/video/661' },
            { key: 'norris', displayName: 'Norris Rink', aliases: ['norris rink', 'norris'], livebarn: 'https://watch.livebarn.com/en/video/660' },
            { key: 'wales', displayName: 'Wales Rink', aliases: ['wales rink', 'wales'], livebarn: 'https://watch.livebarn.com/en/video/659' },
        ],
        livebarn: {
            default: 'https://watch.livebarn.com/en/video/660',
            campbell: 'https://watch.livebarn.com/en/video/661',
            norris: 'https://watch.livebarn.com/en/video/660',
            wales: 'https://watch.livebarn.com/en/video/659',
        },
    }],
    [Rink.MaryvilleUHC, {
        aliases: [
            'maryville university hockey center', 'maryville university',
            'maryville hockey center', 'maryville uhc', 'muhc', 'maryville',
            '18383 chesterfield airport rd'
        ],
        displayName: 'Maryville University Hockey Center',
        address: '18383 Chesterfield Airport Rd, Chesterfield, MO 63005',
        mapsUrl: 'https://maps.google.com/?q=Maryville+University+Hockey+Center+18383+Chesterfield+Airport+Rd+Chesterfield+MO',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'hartwig', displayName: 'Hartwig Kaiman Rink', aliases: ['hartwig kaiman', 'hartwig', 'kaiman'], livebarn: 'https://watch.livebarn.com/en/video/3181' },
            { key: 'shanahan', displayName: 'Shanahan Rink', aliases: ['shanahan rink', 'shanahan'], livebarn: 'https://watch.livebarn.com/en/video/3182' },
        ],
        livebarn: {
            default: 'https://watch.livebarn.com/en/video/3181',
            hartwig: 'https://watch.livebarn.com/en/video/3181',
            'hartwig kaiman': 'https://watch.livebarn.com/en/video/3181',
            shanahan: 'https://watch.livebarn.com/en/video/3182',
        },
    }],
    [Rink.LakeForestAcademy, {
        aliases: ['lake forest academy', 'lake forest', 'lfa', 'mackenzie arena', 'mackenzie ice arena', '1500 w kennedy rd'],
        displayName: 'Lake Forest Academy',
        address: '1500 W Kennedy Rd, Lake Forest, IL 60045',
        mapsUrl: 'https://maps.google.com/?q=Lake+Forest+Academy+1500+W+Kennedy+Rd+Lake+Forest+IL',
        timezone: 'America/Chicago',
        livebarn: { default: 'https://watch.livebarn.com/en/video/450' },
    }],
    [Rink.CentennialIceArena, {
        aliases: [
            'centennial ice arena - wilmette', 'centennial ice arena',
            'centennial ice rinks', 'centennial ice', 'centennial arena',
            'centennial wilmette', 'centennial', '2300 old glenview rd'
        ],
        displayName: 'Centennial Ice Arena',
        address: '2300 Old Glenview Rd, Wilmette, IL 60091',
        mapsUrl: 'https://maps.google.com/?q=Centennial+Ice+Rinks+2300+Old+Glenview+Rd+Wilmette+IL',
        timezone: 'America/Chicago',
        livebarn: { default: 'https://watch.livebarn.com/en/video/1529' },
    }],
    [Rink.Orbit, {
        aliases: ['orbit', 'orbit ice arena', '615 s consumers', '615 consumers'],
        displayName: 'Orbit Ice Arena',
        address: '615 S Consumers Ave, Palatine, IL 60074',
        mapsUrl: 'https://maps.google.com/?q=Orbit+Ice+Arena+615+S+Consumers+Ave+Palatine+IL',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'rink 1', displayName: 'Rink 1', aliases: ['rink 1', 'rink1', 'sheet 1'], livebarn: 'https://watch.livebarn.com/en/video/1225' },
        ],
        livebarn: { default: 'https://watch.livebarn.com/en/video/1225', 'rink 1': 'https://watch.livebarn.com/en/video/1225' }
    }],
    [Rink.Heartland, {
        aliases: ['heartland', 'american heartland', 'heartland ice arena', 'american heartland ice arena', '7384 lincoln', '7384 n lincoln'],
        displayName: 'American Heartland Ice Arena',
        address: '7384 N Lincoln Ave, Lincolnwood, IL 60712',
        mapsUrl: 'https://maps.google.com/?q=American+Heartland+Ice+Arena+7384+N+Lincoln+Ave+Lincolnwood+IL',
        timezone: 'America/Chicago',
        livebarn: null
    }],
    [Rink.WestMeadows, {
        aliases: ['west meadows', 'west meadows ice arena', '3934 winnetka', '3939 winnetka'],
        displayName: 'West Meadows Ice Arena',
        address: '3934 Winnetka Ave, Rolling Meadows, IL 60008',
        mapsUrl: 'https://maps.google.com/?q=West+Meadows+Ice+Arena+3934+Winnetka+Ave+Rolling+Meadows+IL',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'rink 1', displayName: 'Rink 1', aliases: ['rink 1', 'rink1', 'sheet 1'], livebarn: 'https://watch.livebarn.com/en/video/444' },
        ],
        livebarn: { default: 'https://watch.livebarn.com/en/video/444', 'rink 1': 'https://watch.livebarn.com/en/video/444' }
    }],
    [Rink.ElkGrove, {
        aliases: ['elk grove', 'elk grove ice arena', '610 meacham', 'elk grove / east', 'elk grove / west'],
        displayName: 'Elk Grove Ice Arena',
        address: '610 Meacham Rd, Elk Grove Village, IL 60007',
        mapsUrl: 'https://maps.google.com/?q=Elk+Grove+Ice+Arena+610+Meacham+Rd+Elk+Grove+Village+IL',
        timezone: 'America/Chicago',
        sheets: [
            { key: 'east', displayName: 'East Rink', aliases: ['east', 'east rink', 'elk grove / east', 'elk grove east'], livebarn: 'https://watch.livebarn.com/en/video/3288' },
            { key: 'west', displayName: 'West Rink', aliases: ['west', 'west rink', 'elk grove / west', 'elk grove west'], livebarn: 'https://watch.livebarn.com/en/video/3288' },
        ],
        livebarn: { default: 'https://watch.livebarn.com/en/video/3288', east: 'https://watch.livebarn.com/en/video/3288', west: 'https://watch.livebarn.com/en/video/3288' }
    }],
]);

function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Strips street address portions and punctuation that could cause false positive matches
 * (e.g. "Coldbrook Street Northeast", "West Jackson Blvd", "Street East").
 */
function cleanLocationForSheetSearch(location: string): string {
    if (!location) return '';

    // Split by comma, bullet, or pipe
    const parts = location.split(/[,•|]/);

    // Address detection: parts that look like street addresses or city/state/zip
    const addressKeywords = /\b(street|st|avenue|ave|boulevard|blvd|road|rd|drive|dr|highway|hwy|lane|ln|court|ct|way|parkway|pkwy)\b/i;
    const cityStateZip = /\b([A-Z]{2}\s+\d{5}|\d{5})\b/;

    const nonAddressParts = parts.filter(part => {
        const trimmed = part.trim();
        // If it explicitly looks like an address line, exclude it from sheet detection
        if (addressKeywords.test(trimmed) && !/\b(rink|sheet)\b/i.test(trimmed)) {
            return false;
        }
        if (cityStateZip.test(trimmed)) {
            return false;
        }
        return true;
    });

    return (nonAddressParts.length > 0 ? nonAddressParts.join(' ') : location).trim();
}

/**
 * Resolves which sheet within a rink is specified by the location string.
 * Uses strict word-boundary matching and avoids false matches on street address directions.
 */
export function getRinkSheet(location: string, rink: Rink): RinkSheetInfo | null {
    if (!location || rink === Rink.Unknown) return null;
    const rinkData = RINK_DATA.get(rink);
    if (!rinkData || !rinkData.sheets || rinkData.sheets.length === 0) return null;

    const searchableText = cleanLocationForSheetSearch(location).toLowerCase();

    let bestSheet: RinkSheetInfo | null = null;
    let longestMatchLen = 0;

    for (const sheet of rinkData.sheets) {
        const candidates = [
            ...(sheet.aliases || []),
            sheet.displayName.toLowerCase(),
            sheet.key.toLowerCase(),
        ];

        for (const candidate of candidates) {
            if (!candidate) continue;
            // Use word boundary to avoid matching "east" in "northeast", "red" in "infrared", etc.
            const regex = new RegExp(`\\b${escapeRegExp(candidate)}\\b`, 'i');
            if (regex.test(searchableText) && candidate.length > longestMatchLen) {
                bestSheet = sheet;
                longestMatchLen = candidate.length;
            }
        }
    }

    return bestSheet;
}

export function getRink(location: string): Rink {
    if (!location) return Rink.Unknown;
    const lowerLocation = location.toLowerCase();
    let bestRink = Rink.Unknown;
    let longestMatchLen = 0;

    for (const [rink, data] of Array.from(RINK_DATA.entries())) {
        for (const alias of data.aliases) {
            if (lowerLocation.includes(alias) && alias.length > longestMatchLen) {
                bestRink = rink;
                longestMatchLen = alias.length;
            }
        }
    }
    return bestRink;
}